import { useToast } from '@chillibean/component-library';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useWindowSize from '../../../../hook/useWindowSize';
import { StudyEventProcedure } from '../models';
import { setProtocolStudyEventProcedure } from '../redux/actions';
import { createStudyEventProcedure, deleteStudyEventProcedure } from '../services';
import { isPositive } from '../utils';
import { useStudyEventDefinitionsFilteredByStudyArm } from './useStudyEventDefinitionsFilteredByStudyArm';

export const useStudyScheduler = ({ dimensionWidth }) => {
  // HOOKS
  // React
  const [numberOfExtraCells, setNumberOfExtraCells] = useState({
    columns: 8,
    rows: 8,
  });
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [unSelectAllLoading, setUnselectAllLoading] = useState(false);
  const [selectAllDisabled, setSelectAllDisabled] = useState(false);
  // Store
  const dispatch = useDispatch();
  const { account, authenticityToken } = useSelector((state) => state.internalReducer.internal);
  const { data: protocol } = useSelector((state) => state.protocolReducer.protocol);
  const { table: tableData } = protocol || {};

  // Custom
  const { showErrorToast } = useToast();

  const { studyEventDefinitions, studyEventProcedures, studyProcedureDefinitions } = tableData || {};
  const { filteredStudyEventDefinitions, studyEventDefinitionFilteredByStudyArm } =
    useStudyEventDefinitionsFilteredByStudyArm({ studyEventDefinitions });

  const calcNumberOfExtraCells = () => {
    const filledColumns = filteredStudyEventDefinitions?.length || 0;
    const filledStudyProcedureDefinitions = studyProcedureDefinitions?.length || 0;
    const blankColumnWidth = 160;
    const dimensionLimit = dimensionWidth - blankColumnWidth - 100;
    const totalCells = Math.floor(dimensionLimit / 120);
    const numberOfColumns = totalCells - filledColumns;
    const extraCells = { columns: 0, rows: 0 };
    if (isPositive(numberOfColumns)) extraCells.columns = numberOfColumns;
    const numberOfRows = totalCells - filledStudyProcedureDefinitions;
    if (isPositive(numberOfRows)) extraCells.rows = numberOfRows;
    return extraCells;
  };

  const activeStudyEventProcedures = (studyEventDefs) => {
    if (!studyProcedureDefinitions) return [];

    return studyProcedureDefinitions
      .map((studyProcedureDefinition) =>
        studyEventDefs.map((studyEventDefinition) => ({
          studyEventDefinitionId: studyEventDefinition.id,
          studyProcedureDefinitionId: studyProcedureDefinition.id,
        })),
      )
      .flat();
  };

  const findStudyEventProcedureByDefinitionIds = (combinedDefinitions) => {
    return studyEventProcedures.find(
      (studyEventProcedure) =>
        studyEventProcedure.studyEventDefinitionId === combinedDefinitions.studyEventDefinitionId &&
        studyEventProcedure.studyProcedureDefinitionId === combinedDefinitions.studyProcedureDefinitionId,
    );
  };

  const filterActiveCells = ({ studyEventDefs }) => {
    return {
      selectedCells: activeStudyEventProcedures(studyEventDefs)
        .filter((combinedDefinitions) => findStudyEventProcedureByDefinitionIds(combinedDefinitions))
        .map((combinedDefinitions) => findStudyEventProcedureByDefinitionIds(combinedDefinitions)),
      unfilteredSelectedCells: activeStudyEventProcedures(
        (studyEventDefinitions || []).filter(
          (sed) => sed.studyArm?.name !== studyEventDefinitionFilteredByStudyArm?.name && sed.studyArm !== null,
        ),
      )
        .filter((combinedDefinitions) => findStudyEventProcedureByDefinitionIds(combinedDefinitions))
        .map((combinedDefinitions) => findStudyEventProcedureByDefinitionIds(combinedDefinitions)),
      unselectedCells: activeStudyEventProcedures(studyEventDefs).filter(
        (combinedDefinitions) => !findStudyEventProcedureByDefinitionIds(combinedDefinitions),
      ),
    };
  };

  const selectAllCellsAndUpdate = (unselectedCells, selectedCells, unfilteredSelectedCells) => {
    setSelectAllLoading(true);
    Promise.all(
      unselectedCells.map(async (unselectedCell) => {
        const data = await createStudyEventProcedure({
          accountId: account?.id,
          authenticityToken: authenticityToken,
          data: unselectedCell,
          protocolId: protocol?.id,
        });

        return { id: data?.id, ...unselectedCell };
      }),
    )
      .then((results) => {
        dispatch(
          setProtocolStudyEventProcedure([
            ...selectedCells,
            ...unfilteredSelectedCells,
            ...results.map((studyEventProcedure) => new StudyEventProcedure(studyEventProcedure)),
          ]),
        );
        setSelectAllLoading(false);
      })
      .catch((err) => {
        setSelectAllLoading(false);
        showErrorToast(err);
      });
  };

  const selectAllActiveCells = async () => {
    const { selectedCells, unfilteredSelectedCells, unselectedCells } = filterActiveCells({
      studyEventDefs: filteredStudyEventDefinitions,
    });

    if (unselectedCells.length > 0) {
      selectAllCellsAndUpdate(unselectedCells, selectedCells, unfilteredSelectedCells);
    }
  };

  const unselectAllCellsAndUpdate = (selectedCells) => {
    setUnselectAllLoading(true);
    Promise.all(
      selectedCells.map(async (selectedCell) => {
        const data = await deleteStudyEventProcedure({
          accountId: account?.id,
          authenticityToken: authenticityToken,
          protocolId: protocol?.id,
          studyEventProcedureId: selectedCell?.id,
        });

        return data;
      }),
    )
      .then(() => {
        dispatch(setProtocolStudyEventProcedure([]));
        setUnselectAllLoading(false);
      })
      .catch((err) => {
        setUnselectAllLoading(false);
        showErrorToast(err);
      });
  };

  const unselectAllActiveCells = async () => {
    const { selectedCells } = filterActiveCells({ studyEventDefs: filteredStudyEventDefinitions });
    unselectAllCellsAndUpdate(selectedCells);
  };

  const updateNumberOfExtraCells = useCallback(() => {
    const extraCells = calcNumberOfExtraCells();

    if (numberOfExtraCells.columns !== extraCells.columns || numberOfExtraCells.rows !== extraCells.rows)
      setNumberOfExtraCells(extraCells);
  }, [tableData, dimensionWidth]);

  useEffect(() => {
    updateNumberOfExtraCells();
  }, [tableData, dimensionWidth]);

  useEffect(() => {
    const { selectedCells } = filterActiveCells({ studyEventDefs: studyEventDefinitions });

    setSelectAllDisabled(
      selectedCells.length > 0 && selectedCells.length === activeStudyEventProcedures(studyEventDefinitions).length,
    );
  }, [studyEventProcedures]);

  useWindowSize({ resizeDependencies: [tableData, dimensionWidth], resizeFunction: updateNumberOfExtraCells });

  return {
    numberOfExtraCells,
    selectAllActiveCells,
    selectAllDisabled,
    selectAllLoading,
    unSelectAllLoading,
    unselectAllActiveCells,
  };
};

export default useStudyScheduler;
